import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { createCashflowSeries } from "@/api/cashflow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SELECT_CLASSES } from "@/lib/styles";
import {
  CategoryField,
  ErrorPanel,
  FieldError,
  FirmnessField,
  OrgField,
} from "@/pages/cashflow/ManualFields";
import {
  RULE_KIND_OPTIONS,
  WEEKDAY_OPTIONS,
  errorDetail,
  isValidAmount,
  normalizeAmount,
  todayIso,
} from "@/pages/cashflow/utils";
import type { CashflowCategory, CashflowOrg } from "@/types/api";

const schema = z
  .object({
    title: z.string().trim().min(1, "Назовите серию").max(200),
    org_inn: z.string().min(1, "Выберите организацию"),
    category: z.string().min(1, "Выберите статью"),
    firmness: z.enum(["committed", "planned", "assumption"]),
    rule_kind: z.enum(["monthly", "weekly"]),
    amount: z.string().refine(isValidAmount, "Сумма > 0, до двух знаков после запятой"),
    day_of_month: z.string(),
    weekday: z.string(),
    start_date: z.string().min(1, "Укажите дату начала"),
    end_date: z.string(),
    comment: z.string(),
    is_active: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.rule_kind === "monthly") {
      const n = Number(v.day_of_month);
      if (!Number.isInteger(n) || n < 1 || n > 31) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["day_of_month"],
          message: "День месяца — от 1 до 31",
        });
      }
    }
    if (v.end_date && v.end_date < v.start_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["end_date"],
        message: "Дата окончания раньше даты начала",
      });
    }
  });

type Values = z.infer<typeof schema>;

interface Props {
  orgInns: string[];
  orgs: CashflowOrg[];
  categories: CashflowCategory[];
  onCreated: () => void;
  onCancel: () => void;
}

export function ManualSeriesForm({
  orgInns,
  orgs,
  categories,
  onCreated,
  onCancel,
}: Props): React.ReactElement {
  // Ответ сервера держим рядом с формой: 422 с разбором полей по тосту не
  // прочитать, а он исчезает раньше, чем успеваешь понять причину.
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      org_inn: orgInns[0] ?? "",
      category: "",
      firmness: "planned",
      rule_kind: "monthly",
      amount: "",
      day_of_month: "10",
      weekday: "0",
      start_date: todayIso(),
      end_date: "",
      comment: "",
      is_active: true,
    },
  });
  const ruleKind = watch("rule_kind");

  const mut = useMutation({
    mutationFn: (v: Values) =>
      createCashflowSeries({
        title: v.title.trim(),
        org_inn: v.org_inn,
        category: v.category,
        firmness: v.firmness,
        rule_kind: v.rule_kind,
        amount: normalizeAmount(v.amount),
        day_of_month: v.rule_kind === "monthly" ? Number(v.day_of_month) : null,
        weekday: v.rule_kind === "weekly" ? Number(v.weekday) : null,
        start_date: v.start_date,
        end_date: v.end_date === "" ? null : v.end_date,
        comment: v.comment.trim() === "" ? null : v.comment.trim(),
        is_active: v.is_active,
      }),
    onSuccess: () => {
      setServerError(null);
      toast.success("Серия создана");
      reset();
      onCreated();
    },
    onError: (err: unknown) => setServerError(errorDetail(err)),
  });

  return (
    <form
      className="space-y-4 rounded-md border p-4"
      onSubmit={handleSubmit((v) => {
        setServerError(null);
        mut.mutate(v);
      })}
    >
      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="series_title">Название</Label>
          <Input id="series_title" placeholder="напр. Аренда офиса" {...register("title")} />
          <FieldError message={errors.title?.message} />
        </div>
        <OrgField
          id="series_org"
          field={register("org_inn")}
          error={errors.org_inn?.message}
          orgInns={orgInns}
          orgs={orgs}
        />
        <CategoryField
          id="series_category"
          field={register("category")}
          error={errors.category?.message}
          categories={categories}
        />
        <FirmnessField
          id="series_firmness"
          field={register("firmness")}
          error={errors.firmness?.message}
        />
        <div className="space-y-1.5">
          <Label htmlFor="series_rule_kind">Вид правила</Label>
          <select id="series_rule_kind" className={SELECT_CLASSES} {...register("rule_kind")}>
            {RULE_KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {ruleKind === "monthly" ? (
          <div className="space-y-1.5">
            <Label htmlFor="series_day_of_month">День месяца</Label>
            <Input id="series_day_of_month" {...register("day_of_month")} />
            <FieldError message={errors.day_of_month?.message} />
            <p className="text-xs text-muted-foreground">
              Если в месяце меньше дней — платёж встаёт на последний день месяца.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="series_weekday">День недели</Label>
            <select id="series_weekday" className={SELECT_CLASSES} {...register("weekday")}>
              {WEEKDAY_OPTIONS.map((o) => (
                <option key={o.value} value={String(o.value)}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="series_amount">Сумма, ₽</Label>
          <Input id="series_amount" placeholder="100000.00" {...register("amount")} />
          <FieldError message={errors.amount?.message} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="series_start">Дата начала</Label>
          <Input id="series_start" type="date" {...register("start_date")} />
          <FieldError message={errors.start_date?.message} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="series_end">Дата окончания (необязательно)</Label>
          <Input id="series_end" type="date" {...register("end_date")} />
          <FieldError message={errors.end_date?.message} />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="series_comment">Комментарий</Label>
          <Input id="series_comment" {...register("comment")} />
        </div>
        <div className="flex items-end gap-2 pb-2">
          <input
            id="series_is_active"
            type="checkbox"
            className="h-4 w-4"
            {...register("is_active")}
          />
          <Label htmlFor="series_is_active" className="font-normal">
            Активна
          </Label>
        </div>
      </div>

      {serverError !== null && (
        <ErrorPanel detail={`Не удалось создать серию: ${serverError}`} />
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? "Сохранение…" : "Создать серию"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </form>
  );
}
